import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Skeleton } from 'antd';

import { CfFlowStatus } from '../api/cloudflareFlowApi';
import { useCfFlow } from '../useCfFlow';
import { useCfStepEvents } from '../useCfStepEvents';
import type { DomainsProbedThisPage } from '../cfProbedThisPage';
import { useFlowResumed } from '../useFlowResumed';
import { useReadyEmailClicked } from '../useReadyEmailClicked';
import { useCfScreenTrail } from '../useCfScreenTrail';
import { useStartCfSetupMutation } from '../api/cloudflareSetupApi';
import { useVerifyCfIntegrationMutation } from '../api/cloudflareVerificationApi';
import { FLOW_READ_ERROR_DESCRIPTION, FLOW_READ_ERROR_MESSAGE } from './cfFlowReadErrorCopy';
import CloudflareBlockedStep from './CloudflareBlockedStep';
import CloudflareFlowScreen from './CloudflareFlowScreen';
import CloudflareProgressStep from './CloudflareProgressStep';
import CloudflareTokenStep from './CloudflareTokenStep';
import { BETA_CLOSED_REASON, BETA_CLOSED_TITLE } from './betaClosedCopy';
import { CfAccountConfirmationFailure, useCfAccountConfirmation } from './useCfAccountConfirmation';
import { CfPropagationRecheckProblem, useCfPropagationRecheck } from './useCfPropagationRecheck';
import { useCfVerificationRetry } from './useCfVerificationRetry';
import { type CfNexusOpening, type CfNexusScene, cfNexusSceneOf } from '../cfNexusOpening';
import { cfEventSubjectOf } from '../cfEventProperties';
import { screenNexusPrompt } from '../nexus/screenNexusPrompt';
import { usePrerenderToken } from '../../../hooks/usePrerenderUser';

/**
 * The states in which the server waits to be asked for the next step rather
 * than already running it: a token accepted but no install started, and a
 * deployment landed that nothing has looked at yet.
 */
const AUTO_ADVANCED: readonly CfFlowStatus[] = ['token_connected', 'verifying'];

/**
 * A token reconnected on these resumes the setup the dead token stopped. Only a
 * `revoked` one: the retention sweep flags failed flows too, and re-running a
 * setup that failed for another reason changes the customer's account again.
 */
const RESUMED_ON_RECONNECT: readonly CfFlowStatus[] = ['preflight_failed', 'deploy_failed'];

const RECHECK_PROBLEM_COPY: Readonly<
  Record<CfPropagationRecheckProblem, { message: string; description?: string; retryable: boolean }>
> = {
  cloudflare_unreachable: {
    message: "We couldn't reach Cloudflare to check your domain. Try again in a moment.",
    retryable: true,
  },
  throttled: {
    message: 'You have checked several times in a row',
    description: 'Wait a minute before trying again.',
    retryable: false,
  },
  request_failed: {
    message: 'We could not check your domain',
    description: 'Your request did not reach Prerender. Check your connection and try again.',
    retryable: true,
  },
  beta_closed: {
    message: BETA_CLOSED_TITLE,
    description: BETA_CLOSED_REASON,
    retryable: false,
  },
};

const ACCOUNT_CONFIRMATION_FAILURE_COPY: Readonly<
  Record<CfAccountConfirmationFailure, { message: string; description: string; retryable: boolean }>
> = {
  request_failed: {
    message: 'We could not confirm your domain was added',
    description: 'Your answer did not reach Prerender. Check your connection and try again.',
    retryable: true,
  },
  server_error: {
    message: 'We could not confirm your domain was added',
    description: 'Prerender could not save your answer. Try again in a moment.',
    retryable: true,
  },
  throttled: {
    message: 'You have tried several times in a row',
    description: 'Wait a minute before trying again.',
    retryable: false,
  },
  no_flow: {
    message: 'We could not find the setup for this domain',
    description: 'Go back to the first step and analyze your domain again.',
    retryable: false,
  },
  domain_rejected: {
    message: 'This domain cannot be set up automatically',
    description: 'Go back to the first step and check the domain you entered.',
    retryable: false,
  },
};

export type CloudflareFlowScreensProps = {
  domain: string;
  onGoBack?: () => void;
  onFinish?: () => void;
  onOpenNexus?: (opening: CfNexusOpening) => void;
  onSceneChange?: (scene: CfNexusScene | null) => void;
  probedThisPage?: DomainsProbedThisPage;
};

/**
 * Never derive `screen` here: a customer opening the propagation email on
 * another device has no `localStorage` to be placed from.
 */
const CloudflareFlowScreens = ({
  domain,
  onGoBack,
  onFinish,
  onOpenNexus: onOpenHostNexus,
  onSceneChange,
  probedThisPage,
}: CloudflareFlowScreensProps) => {
  // Counted here rather than read from `propagationRecheck.isChecking`: that hook
  // needs the screen, and the screen needs this read.
  const [propagationChecksInFlight, setPropagationChecksInFlight] = useState(0);
  const { flow, isLoading, isError, refetch } = useCfFlow(domain, { checking: propagationChecksInFlight > 0 });
  const [startSetup] = useStartCfSetupMutation();
  const [verifyIntegration] = useVerifyCfIntegrationMutation();

  useFlowResumed(flow, { probedThisPage });
  useReadyEmailClicked(flow);

  const trail = useCfScreenTrail(flow?.screen);

  const onTheNameserverStep = trail.screen === 'nameservers';
  const onTheWaitingScreen = trail.screen === 'propagation';

  const propagationRecheck = useCfPropagationRecheck(domain, onTheWaitingScreen);
  const nameserverRecheck = useCfPropagationRecheck(domain, onTheNameserverStep);

  const shownScreen = isLoading || isError ? null : (trail.screen ?? null);

  useCfStepEvents(flow, shownScreen);

  const subject = useMemo(
    () => (flow ? cfEventSubjectOf(flow) : undefined),
    // The flow object is replaced on every poll; only what the subject reads should move it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flow?.domain, flow?.status, flow?.subCase, flow?.entrySource],
  );

  const onOpenNexus = useMemo(
    () =>
      onOpenHostNexus &&
      subject &&
      ((opening: CfNexusOpening) => onOpenHostNexus({ ...opening, domain: subject.domain, subject })),
    [onOpenHostNexus, subject],
  );

  const advancedForStatus = useRef<CfFlowStatus | null>(null);

  const [failedAdvance, setFailedAdvance] = useState<CfFlowStatus | null>(null);

  const retrySetup = useCallback(async () => {
    // The server answers a refusal with 200; only a transport error lands in the catch.
    await startSetup({ domain }).unwrap().catch(() => undefined);
    refetch();
  }, [domain, refetch, startSetup]);

  const checkVerification = useCallback(async () => {
    try {
      const { verified } = await verifyIntegration({ domain }).unwrap();

      return verified;
    } finally {
      // The call may have moved the flow even when the response never arrived.
      refetch();
    }
  }, [domain, refetch, verifyIntegration]);

  const checkVerificationAgain = useCallback(() => {
    // The check moves the server back to `verifying`, which must not count as a new status to advance.
    advancedForStatus.current = 'verifying';

    return checkVerification();
  }, [checkVerification]);

  const verificationInPlay = flow?.status === 'verifying' || flow?.status === 'verification_failed';

  const {
    retry: verificationRetry,
    schedule: scheduleVerificationRetry,
    runNow: retryVerificationNow,
  } = useCfVerificationRetry(checkVerificationAgain, verificationInPlay);

  const prerenderToken = usePrerenderToken();
  const prefillText = flow
    ? screenNexusPrompt({ flow, screen: shownScreen, verificationRetry, knownSecrets: [prerenderToken] })
    : undefined;

  const scene = useMemo(
    () => (flow && shownScreen ? { ...cfNexusSceneOf(flow, shownScreen), prefillText } : null),
    // The flow object is replaced on every poll; only what the scene reads should move it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [shownScreen, flow?.status, flow?.domain, flow?.subCase, flow?.entrySource, flow?.errorDetail?.reason, prefillText],
  );

  const [tokenErrorShown, setTokenErrorShown] = useState<string | null>(null);

  const sceneOnScreen = useMemo(
    () => (scene && tokenErrorShown ? { ...scene, errorShown: tokenErrorShown } : scene),
    [scene, tokenErrorShown],
  );

  useEffect(() => {
    onSceneChange?.(sceneOnScreen);
  }, [sceneOnScreen, onSceneChange]);

  const startSetupFrom = useCallback(
    async (status: CfFlowStatus) => {
      setFailedAdvance(null);

      try {
        const { refusal } = await startSetup({ domain }).unwrap();
        if (refusal) setFailedAdvance(status);
      } catch {
        // A failed request is not retried on a timer: each call runs a whole
        // install against the customer's Cloudflare rate budget.
        setFailedAdvance(status);
      } finally {
        refetch();
      }
    },
    [domain, refetch, startSetup],
  );

  const advance = useCallback(
    async (status: CfFlowStatus) => {
      if (status !== 'verifying') return startSetupFrom(status);

      setFailedAdvance(null);
      const verified = await checkVerification().catch(() => false);
      if (!verified) scheduleVerificationRetry();
    },
    [checkVerification, startSetupFrom, scheduleVerificationRetry],
  );

  useEffect(() => {
    const status = flow?.status;
    if (!status) return;

    if (!AUTO_ADVANCED.includes(status)) {
      advancedForStatus.current = null;

      return;
    }

    if (advancedForStatus.current === status) return;
    advancedForStatus.current = status;

    advance(status);
  }, [flow?.status, advance]);

  const goBack = trail.canGoBack ? trail.goBack : onGoBack;

  const thenMoveOn = (action: () => unknown) => () => {
    trail.goForward();
    action();
  };

  const onTheAccountStep = trail.screen === 'cf_account';

  const accountConfirmation = useCfAccountConfirmation(domain, onTheAccountStep);

  const confirmAccount = async () => {
    if (!(await accountConfirmation.confirm())) return;

    trail.goForward();
    refetch();
  };

  const confirmNameservers = async () => {
    if (!(await nameserverRecheck.check()).ok) return;

    trail.goForward();
    refetch();
  };

  const checkPropagation = async ({ inBackground }: { inBackground: boolean }) => {
    setPropagationChecksInFlight((count) => count + 1);
    try {
      const { movedOn } = await propagationRecheck.check({ inBackground });
      refetch();

      return movedOn;
    } finally {
      setPropagationChecksInFlight((count) => count - 1);
    }
  };

  const checkPropagationAgain = () => checkPropagation({ inBackground: false });

  const recheckOnScreen =
    trail.screen === 'nameservers'
      ? { problem: nameserverRecheck.problem, retry: confirmNameservers, isChecking: nameserverRecheck.isChecking }
      : trail.screen === 'propagation'
        ? { problem: propagationRecheck.problem, retry: checkPropagationAgain, isChecking: propagationRecheck.isChecking }
        : null;

  const recheckProblem = recheckOnScreen?.problem ? RECHECK_PROBLEM_COPY[recheckOnScreen.problem] : null;

  const accountConfirmationFailure =
    onTheAccountStep && accountConfirmation.failure
      ? ACCOUNT_CONFIRMATION_FAILURE_COPY[accountConfirmation.failure]
      : null;

  if (isLoading) return <Skeleton active paragraph={{ rows: 6 }} />;

  if (isError || !flow) {
    return (
      <Alert
        type="error"
        showIcon
        message={FLOW_READ_ERROR_MESSAGE}
        description={FLOW_READ_ERROR_DESCRIPTION}
      />
    );
  }

  const progress = (
    <CloudflareProgressStep
      flow={flow}
      verificationRetry={verificationRetry}
      onRetryVerification={retryVerificationNow}
      onGoBack={goBack}
      onOpenNexus={onOpenNexus}
      onRetrySetup={thenMoveOn(retrySetup)}
    />
  );

  const resumesOnReconnect = flow.reconnectReason === 'revoked' && RESUMED_ON_RECONNECT.includes(flow.status);

  const tokenStep = (fromStep: 'token' | 'reconnect') => (
    <CloudflareTokenStep
      fromStep={fromStep}
      domain={domain}
      tokenTemplateUrl={flow.tokenTemplateUrl}
      reconnectReason={flow.reconnectReason ?? undefined}
      retentionDays={flow.tokenRetentionDays}
      initialAttemptNumber={flow.attemptNumber}
      onConnected={thenMoveOn(resumesOnReconnect ? () => startSetupFrom(flow.status) : refetch)}
      onGoBack={goBack}
      onOpenNexus={onOpenNexus}
      onErrorShownChange={setTokenErrorShown}
    />
  );

  return (
    <>
      {failedAdvance === flow.status && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="We could not continue the setup"
          description="Nothing in your Cloudflare account was left half-changed. Try again when you are ready."
          action={
            <Button size="small" onClick={() => advance(failedAdvance)}>
              Try again
            </Button>
          }
        />
      )}
      {accountConfirmationFailure && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={accountConfirmationFailure.message}
          description={accountConfirmationFailure.description}
          action={
            accountConfirmationFailure.retryable && (
              <Button size="small" onClick={confirmAccount} loading={accountConfirmation.isConfirming}>
                Try again
              </Button>
            )
          }
        />
      )}
      {recheckOnScreen && recheckProblem && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={recheckProblem.message}
          description={recheckProblem.description}
          action={
            recheckProblem.retryable && (
              <Button size="small" onClick={recheckOnScreen.retry} loading={recheckOnScreen.isChecking}>
                Try again
              </Button>
            )
          }
        />
      )}
      <CloudflareFlowScreen
        flow={flow}
        screen={trail.screen}
        isCheckingNameservers={nameserverRecheck.isChecking}
        isConfirmingAccount={accountConfirmation.isConfirming}
        verificationRetry={verificationRetry}
        onRetryVerification={retryVerificationNow}
        // `abandoned` is not expected here; the progress list beats a blank panel.
        slots={{
          blocked: <CloudflareBlockedStep blockReason={null} domain={domain} onGoBack={goBack} />,
          token: tokenStep('token'),
          reconnect: tokenStep('reconnect'),
          abandoned: progress,
        }}
        onCheckPropagation={checkPropagation}
        onConfirmAccount={confirmAccount}
        onConfirmNameservers={confirmNameservers}
        onOpenNexus={onOpenNexus}
        onRetrySetup={thenMoveOn(retrySetup)}
        onGoBack={goBack}
        onFinish={onFinish}
      />
    </>
  );
};

export default CloudflareFlowScreens;
